import { useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { incompleteOrdersMsg, salesFormsMsg, t } from '../../../common/i18n/index.js';
import { type AuthProps } from '../../../public/site/AuthProvider.js';
import { useEventListener } from '../OpenFormHooks/useEventListener.js';
import { type OpenFormAccount, useOpenFormAccount } from '../OpenFormHooks/useOpenFormAccount.js';
import { isPreview, useOpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { useReload } from '../OpenFormHooks/useReload.js';
import { useOpenFormAsync, useOpenFormState } from '../OpenFormProvider.js';
import { concat } from '../OpenFormUtils.js';
import type { AuthenticatedUser } from '../../../public/site/authState';

export type OpenFormOutlet = {
  account?: OpenFormAccount;
  preview?: boolean;
  reload?: () => void;
  title?: (path?: string) => string | undefined;
  user?: AuthenticatedUser;
};

export const OpenFormLayoutOutlet = ({ auth }: { auth: AuthProps }) => {
  const { pathname } = useLocation();
  const { form } = useOpenFormState();
  const async = useOpenFormAsync();
  const goto = useOpenFormGoto();
  const account = useOpenFormAccount();
  const preview = useMemo(() => isPreview(pathname), [pathname]);
  const [reloading, reload] = useReload();

  const title = useCallback(
    (path = pathname) => {
      switch (path) {
        case paths.OPENFORM_LIST_DRAFT:
          return t.E99N(incompleteOrdersMsg);
        case paths.OPENFORM_LIST_FORM:
          return t.QF2O(salesFormsMsg);
        default:
          return form?.name && concat(form.name, form.referenceNumber).join(' • ');
      }
    },
    [pathname, form]
  );

  useEventListener(
    'message',
    useCallback(
      ({ data }: MessageEvent) => {
        switch (data?.action) {
          case 'billingAccountCreated':
          case 'contactCreated':
            return async.frame.postMessageAcknowledge(data.action); // Placeholder
          case 'goto':
            return goto(data);
        }
      },
      [async, goto]
    )
  );

  return reloading ? null : <Outlet context={{ account, preview, reload, title, user: auth.authenticatedUser }} />;
};
